import { Fragment, useCallback, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { SectionHeader, EmptyState, Spinner, DataTable } from '../../components/ui';
import { brokerApi } from '../../api/brokerClient';
import { usePaperPositions } from '../../api/hooks';
import { ROUTES } from '../../routes/constants';
import { computeUnrealizedPnl, fmtValue, fmtPnl, normalizePositionRow } from './bookUtils';
import { useLtpStore } from '../../store/ltpStore';

// ─── Broker position columns ─────────────────────────────────────────────────
const pnlRender = (v) => {
  const num = Number(v);
  const cls = num > 0 ? 'text-bull' : num < 0 ? 'text-bear' : 'text-txt-secondary';
  return <span className={cls}>{fmtPnl(v)}</span>;
};

function ltpSymbolCandidates(symbol, exchange) {
  const raw = String(symbol || '').trim().toUpperCase();
  if (!raw) return [];
  const normalizedExchange = String(exchange || 'NSE').toUpperCase().replace(/_CM$|_FO$/i, '');
  const ticker = raw.includes(':') ? raw.split(':').pop() : raw;
  const candidates = [raw.includes(':') ? raw : `${normalizedExchange}:${ticker}`];
  if (/(\d+(CE|PE)|FUT)$/.test(ticker)) {
    candidates.push(`NFO:${ticker}`);
    candidates.push(`NSE:${ticker}`);
    candidates.push(ticker);
  }
  return Array.from(new Set(candidates.filter(Boolean)));
}

const brokerColumns = [
  { key: 'symbol',        label: 'Symbol',        render: (v) => fmtValue(v) },
  { key: 'product',       label: 'Product',       render: (v) => fmtValue(v), mobileHidden: true },
  { key: 'buyQty',        label: 'Buy Qty',       render: (v) => fmtValue(v) },
  { key: 'buyAvg',        label: 'Buy Av',        render: (v) => fmtValue(v), mobileHidden: true },
  { key: 'sellQty',       label: 'Sell Qty',      render: (v) => fmtValue(v) },
  { key: 'sellAvg',       label: 'Sell Av',       render: (v) => fmtValue(v), mobileHidden: true },
  { key: 'netQty',        label: 'Net Qty',       render: (v) => fmtValue(v) },
  { key: 'ltp',           label: 'LTP',           render: (v) => fmtValue(v), width: '5.5rem' },
  { key: 'realizedPnl',   label: 'Realized PL',   render: pnlRender },
  { key: 'unrealizedPnl', label: 'Unrealized PL', render: pnlRender },
  { key: 'pnl',           label: 'Total PL',      render: pnlRender },
];

function pnlClass(value) {
  const num = Number(value);
  return value == null || value === '' || !Number.isFinite(num)
    ? 'text-txt-secondary'
    : num > 0
      ? 'text-bull'
      : num < 0
        ? 'text-bear'
        : 'text-txt-secondary';
}

function BrokerPositionStatusBadge({ row }) {
  const isFlat = Number(row?.netQty ?? row?.net_qty ?? 0) === 0;
  return (
    <span className={
      'badge border ' +
      (isFlat ? 'bg-bg-hover border-bg-border text-txt-muted' : 'bg-bull/10 border-bull/30 text-bull')
    }>
      {isFlat ? 'CLOSED' : 'OPEN'}
    </span>
  );
}

function BrokerPositionCard({ row }) {
  const netQty = Number(row?.netQty ?? row?.net_qty ?? 0);
  const isFlat = netQty === 0;
  return (
    <div className="rounded-md border border-bg-border bg-bg-surface/40 p-1.5">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="font-semibold text-txt-primary truncate min-w-0">{fmtValue(row.symbol)}</span>
        <BrokerPositionStatusBadge row={row} />
      </div>

      <div className="text-2xs font-mono text-txt-muted mb-1">
        {row.product != null && row.product !== '' && <span>{fmtValue(row.product)} · </span>}
        Net Qty: <span className="text-txt-secondary">{fmtValue(row.netQty)}</span>
        {' · '}Buy: <span className="text-txt-secondary">{fmtValue(row.buyAvg)}</span>
        {' · '}Sell: <span className="text-txt-secondary">{fmtValue(row.sellAvg)}</span>
      </div>

      <div className="flex items-baseline justify-between gap-3 mb-1">
        <span className="text-2xs font-mono uppercase tracking-widest text-txt-label">Total P&L</span>
        <span className={`text-base font-bold font-mono ${pnlClass(row.pnl)}`}>₹{fmtPnl(row.pnl)}</span>
      </div>

      {!isFlat && (
        <div className="text-2xs font-mono text-txt-muted">
          Realized <span className={pnlClass(row.realizedPnl)}>₹{fmtPnl(row.realizedPnl)}</span>
          {' · '}Unrealized <span className={pnlClass(row.unrealizedPnl)}>₹{fmtPnl(row.unrealizedPnl)}</span>
        </div>
      )}

      {!isFlat && (
        <div className="mt-1.5 text-xs font-mono text-txt-muted">
          LTP <span className="text-txt-primary font-medium">{fmtValue(row.ltp)}</span>
        </div>
      )}
    </div>
  );
}

const paperColumns = [
  {
    key: 'symbol',
    label: 'Symbol',
    render: (v, row) => (
      <span className="inline-flex items-center gap-1.5">
        <span>{fmtValue(v)}</span>
        {row?.carryForward ? (
          <span
            title={`Carried forward from ${row.openedDateIst}`}
            className="inline-flex items-center justify-center rounded-full bg-amber/10 text-amber text-2xs px-1.5 py-0.5 border border-amber/35 leading-none"
          >
            ↻
          </span>
        ) : null}
      </span>
    ),
  },
  { key: 'product',       label: 'Product',       render: (v) => fmtValue(v), mobileHidden: true },
  { key: 'buyQty',        label: 'Buy Qty',       render: (v) => fmtValue(v) },
  { key: 'buyAvg',        label: 'Buy Avg',       render: (v) => fmtValue(v), mobileHidden: true },
  { key: 'sellQty',       label: 'Sell Qty',      render: (v) => fmtValue(v) },
  { key: 'sellAvg',       label: 'Sell Avg',      render: (v) => fmtValue(v), mobileHidden: true },
  { key: 'netQty',        label: 'Net Qty',       render: (v) => fmtValue(v) },
  { key: 'ltp',           label: 'LTP',           render: (v) => fmtValue(v), width: '5.5rem' },
  { key: 'realizedPnl',   label: 'Realized PL',   render: pnlRender },
  { key: 'unrealizedPnl', label: 'Unrealized PL', render: pnlRender },
];

const UNATTACHED_KEY = '__unattached__';

function groupPaperRows(rows) {
  const buckets = new Map();
  for (const row of rows) {
    const key = row.strategyName ? `s:${row.strategyId ?? row.strategyName}` : UNATTACHED_KEY;
    const label = row.strategyName || 'Unattached';
    if (!buckets.has(key)) {
      buckets.set(key, { key, label, rows: [], realized: 0, unrealized: 0, hasRealized: false, hasUnrealized: false });
    }
    const g = buckets.get(key);
    g.rows.push(row);
    if (row.realizedPnl != null && row.realizedPnl !== '') {
      g.realized += Number(row.realizedPnl) || 0;
      g.hasRealized = true;
    }
    if (row.unrealizedPnl != null && row.unrealizedPnl !== '') {
      g.unrealized += Number(row.unrealizedPnl) || 0;
      g.hasUnrealized = true;
    }
  }
  for (const g of buckets.values()) {
    g.rows.sort((a, b) => String(a.symbol || '').localeCompare(String(b.symbol || '')));
  }
  return Array.from(buckets.values()).sort((a, b) => {
    if (a.key === UNATTACHED_KEY) return 1;
    if (b.key === UNATTACHED_KEY) return -1;
    return Math.abs(b.realized + b.unrealized) - Math.abs(a.realized + a.unrealized);
  });
}

function GroupedPaperPositionsTable({ columns, rows, emptyText }) {
  const groups = useMemo(() => groupPaperRows(rows), [rows]);
  const [collapsed, setCollapsed] = useState(() => new Set());

  if (!rows?.length) {
    return <EmptyState title={emptyText || 'No records found'} />;
  }

  const toggle = (key) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const colCount = columns.length;
  // Subtotal P/L cells line up with the realizedPnl / unrealizedPnl columns.
  const realizedIdx = columns.findIndex((c) => c.key === 'realizedPnl');
  const unrealizedIdx = columns.findIndex((c) => c.key === 'unrealizedPnl');
  const headerSpanLen = realizedIdx > 0 ? realizedIdx : colCount;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs font-mono">
        <thead>
          <tr className="border-b border-bg-border">
            {columns.map((col) => (
              <th
                key={col.key}
                className={
                  'text-left py-2 px-3 text-txt-label uppercase tracking-widest text-2xs font-semibold' +
                  (col.mobileHidden ? ' hidden sm:table-cell' : '')
                }
                style={{ width: col.width }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const isCollapsed = collapsed.has(g.key);
            return (
              <Fragment key={g.key}>
                <tr
                  onClick={() => toggle(g.key)}
                  className="border-b border-bg-border/70 bg-bg-hover/40 cursor-pointer hover:bg-bg-hover/70"
                >
                  <td
                    colSpan={headerSpanLen}
                    className="py-1 px-3 text-info font-bold uppercase tracking-wide text-xs"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                      <span>{g.label}</span>
                    </span>
                  </td>
                  {columns.slice(headerSpanLen).map((col, i) => {
                    const absIdx = headerSpanLen + i;
                    let content = null;
                    if (absIdx === realizedIdx) {
                      content = g.hasRealized ? pnlRender(g.realized) : '—';
                    } else if (absIdx === unrealizedIdx) {
                      content = g.hasUnrealized ? pnlRender(g.unrealized) : '—';
                    }
                    return (
                      <td
                        key={col.key}
                        className={
                          'py-1 px-3 font-semibold' +
                          (col.mobileHidden ? ' hidden sm:table-cell' : '')
                        }
                      >
                        {content}
                      </td>
                    );
                  })}
                </tr>
                {!isCollapsed && g.rows.map((row, i) => (
                  <tr
                    key={row.id ?? `${g.key}-${i}`}
                    className="border-b border-bg-border/40"
                  >
                    {columns.map((col, ci) => (
                      <td
                        key={col.key}
                        className={
                          'py-1 px-3 text-txt-secondary' +
                          (col.mobileHidden ? ' hidden sm:table-cell' : '') +
                          (ci === 0 ? ' pl-8' : '')
                        }
                      >
                        {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                      </td>
                    ))}
                  </tr>
                ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PaperPositionStatusBadge({ row }) {
  const isClosed = Number(row?.netQty ?? row?.net_qty ?? 0) === 0;
  return (
    <span className={
      'badge border ' +
      (isClosed ? 'bg-bg-hover border-bg-border text-txt-muted' : 'bg-bull/10 border-bull/30 text-bull')
    }>
      {isClosed ? 'CLOSED' : 'OPEN'}
    </span>
  );
}

function PaperPositionCard({ row }) {
  const isClosed = Number(row?.netQty ?? row?.net_qty ?? 0) === 0;
  return (
    <div className="rounded-md border border-bg-border bg-bg-surface/40 p-1.5">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="inline-flex items-center gap-1.5 min-w-0">
          <span className="font-semibold text-txt-primary truncate min-w-0">{fmtValue(row.symbol)}</span>
          {row?.carryForward ? (
            <span
              title={`Carried forward from ${row.openedDateIst}`}
              className="inline-flex items-center justify-center rounded-full bg-amber/10 text-amber text-2xs px-1.5 py-0.5 border border-amber/35 leading-none shrink-0"
            >
              ↻
            </span>
          ) : null}
        </span>
        <PaperPositionStatusBadge row={row} />
      </div>

      <div className="text-2xs font-mono text-txt-muted mb-1">
        {row.product != null && row.product !== '' && <span>{fmtValue(row.product)} · </span>}
        Net Qty: <span className="text-txt-secondary">{fmtValue(row.netQty)}</span>
        {' · '}Buy: <span className="text-txt-secondary">{fmtValue(row.buyAvg)}</span>
        {' · '}Sell: <span className="text-txt-secondary">{fmtValue(row.sellAvg)}</span>
      </div>

      <div className="flex items-baseline justify-between gap-3 mb-1">
        <span className="text-2xs font-mono uppercase tracking-widest text-txt-label">Total P&L</span>
        <span className={`text-base font-bold font-mono ${pnlClass(row.pnl)}`}>₹{fmtPnl(row.pnl)}</span>
      </div>

      {!isClosed && (
        <div className="text-2xs font-mono text-txt-muted">
          Realized <span className={pnlClass(row.realizedPnl)}>₹{fmtPnl(row.realizedPnl)}</span>
          {' · '}Unrealized <span className={pnlClass(row.unrealizedPnl)}>₹{fmtPnl(row.unrealizedPnl)}</span>
        </div>
      )}

      {!isClosed && (
        <div className="mt-1.5 text-xs font-mono text-txt-muted">
          LTP <span className="text-txt-primary font-medium">{fmtValue(row.ltp)}</span>
        </div>
      )}
    </div>
  );
}

function GroupedPaperPositionsCards({ rows, emptyText }) {
  const groups = useMemo(() => groupPaperRows(rows), [rows]);

  if (!rows?.length) {
    return <EmptyState title={emptyText || 'No records found'} />;
  }

  return (
    <div className="space-y-3">
      {groups.map((group) => (
        <section key={group.key} className="space-y-1.5">
          <div className="flex items-center justify-between gap-2 px-0.5">
            <span className="text-2xs font-mono uppercase tracking-widest text-info font-semibold truncate">
              {group.label}
            </span>
            <span className="text-2xs font-mono text-txt-muted shrink-0">
              {group.rows.length} {group.rows.length === 1 ? 'row' : 'rows'}
            </span>
          </div>
          <div className="space-y-1.5">
            {group.rows.map((row, i) => (
              <PaperPositionCard key={row.id ?? `${group.key}-${i}`} row={row} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export function PositionsBody({ onRefreshChange } = {}) {
  const navigate = useNavigate();
  const [posTab, setPosTab] = useState('live');
  const [paperDate, setPaperDate] = useState(''); // '' = today/active view
  const todayIst = useMemo(() => {
    const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' });
    return fmt.format(new Date()); // YYYY-MM-DD
  }, []);
  const historicalDate = paperDate && paperDate !== todayIst ? paperDate : null;

  // Broker positions (existing query — unchanged)
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['brokerPositionsPage'],
    queryFn: brokerApi.getPositions,
    staleTime: 30_000,
    refetchInterval: false,
    retry: 1,
    enabled: posTab === 'live',
  });
  const brokerRows = Array.isArray(data?.positions) ? data.positions.map(normalizePositionRow) : [];

  // Paper positions
  const paperPos = usePaperPositions(historicalDate, { enabled: posTab === 'paper' });
  const paperRaw = Array.isArray(paperPos.data?.positions) ? paperPos.data.positions : [];
  const paperRows = paperRaw.map((p, i) => {
    const norm = normalizePositionRow(p, i);
    // IST date of opened_at, for the carry-forward badge on the active view.
    let openedDateIst = null;
    if (p.opened_at) {
      const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' });
      openedDateIst = fmt.format(new Date(p.opened_at));
    }
    return {
      ...norm,
      strategyId: p.strategy_id ?? null,
      strategyName: p.strategy_name ?? null,
      deploymentId: p.deployment_id ?? null,
      openedDateIst,
      carryForward: !historicalDate && openedDateIst && openedDateIst !== todayIst,
    };
  });
  const paperViewMode = paperPos.data?.view_mode ?? (historicalDate ? 'historical' : 'active');
  const paperAsOfDate = paperPos.data?.as_of_date ?? (historicalDate || todayIst);

  // Subscribe open position symbols to live LTP via WebSocket
  const ltpMap = useLtpStore((s) => s.ltp);
  const subscribe = useLtpStore((s) => s.subscribe);
  const unsubscribe = useLtpStore((s) => s.unsubscribe);

  const openSymbols = useMemo(
    () => brokerRows.filter((r) => r.netQty !== 0).flatMap((r) => ltpSymbolCandidates(r.symbol, r.exchange)),
    [brokerRows],
  );

  const openSymbolsKey = JSON.stringify(openSymbols);

  useEffect(() => {
    if (posTab !== 'live') return;
    if (openSymbols.length === 0) return;
    subscribe(openSymbols);
    return () => unsubscribe(openSymbols);
  }, [openSymbolsKey, posTab]);

  // Subscribe open paper position symbols to live LTP via WebSocket
  const openPaperSymbols = useMemo(
    () => paperRows.filter((r) => r.netQty !== 0).map((r) => r.symbol).filter(Boolean),
    [paperRows],
  );

  const openPaperSymbolsKey = JSON.stringify(openPaperSymbols);

  useEffect(() => {
    if (posTab !== 'paper') return;
    if (historicalDate) return; // historical snapshots don't need live LTP
    if (openPaperSymbols.length === 0) return;
    subscribe(openPaperSymbols);
    return () => unsubscribe(openPaperSymbols);
  }, [openPaperSymbolsKey, posTab, historicalDate]);

  // Merge live LTP into rows for PnL recomputation
  const enrichedRows = useMemo(() => brokerRows.filter((r) => r.symbol != null).map((row) => {
    const liveLtp = ltpSymbolCandidates(row.symbol, row.exchange)
      .map((key) => ltpMap[key])
      .find((value) => value != null);
    if (liveLtp == null || row.netQty === 0) return row;
    const ltp = Number(liveLtp);
    const unrealizedPnl = computeUnrealizedPnl(row, ltp);
    return {
      ...row,
      ltp,
      unrealizedPnl,
      pnl: (Number(row.realizedPnl) || 0) + unrealizedPnl,
    };
  }), [brokerRows, ltpMap]);

  // Merge live LTP into paper rows for unrealized PnL
  const enrichedPaperRows = useMemo(() => paperRows.map((row) => {
    const liveLtp = ltpMap[row.symbol];
    if (liveLtp == null || row.netQty === 0) return row;
    const ltp = Number(liveLtp);
    const unrealizedPnl = computeUnrealizedPnl(row, ltp);
    return { ...row, ltp, unrealizedPnl };
  }), [paperRows, ltpMap]);
  const handleRefresh = useCallback(() => {
    if (posTab === 'live') return refetch();
    return paperPos.refetch();
  }, [posTab, refetch, paperPos.refetch]);

  useEffect(() => {
    onRefreshChange?.(handleRefresh);
    return () => onRefreshChange?.(null);
  }, [onRefreshChange, handleRefresh]);

  return (
    <>
      {/* ── Position Tab Bar ──────────────────────────────────────────────── */}
      <div className="flex gap-1 bg-bg-hover/30 rounded-lg p-1 w-fit">
        <button
          onClick={() => setPosTab('live')}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            posTab === 'live'
              ? 'bg-bg-elevated text-txt-primary shadow-sm'
              : 'text-txt-secondary hover:text-txt-primary'
          }`}
        >
          Live
        </button>
        <button
          onClick={() => setPosTab('paper')}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            posTab === 'paper'
              ? 'bg-info/10 text-info shadow-sm'
              : 'text-txt-secondary hover:text-txt-primary'
          }`}
        >
          Paper
        </button>
      </div>

      {/* ── Broker Positions (Live tab) ───────────────────────────────────── */}
      {posTab === 'live' && (
        <div className="rounded-lg border border-transparent bg-transparent p-0 lg:border-bg-border lg:bg-bg-panel lg:p-4 lg:shadow-panel">
          {isLoading ? (
            <div className="flex justify-center py-12"><Spinner size={18} /></div>
          ) : error ? (
            <EmptyState title="Could not load broker positions" description={error.message} />
          ) : enrichedRows.length === 0 ? (
            <EmptyState
              title="No broker positions yet"
              description="Positions from your connected broker will appear here once a live strategy is running."
              action={(
                <button onClick={() => navigate(ROUTES.STRATEGIES)} className="btn-secondary mt-4 text-xs">
                  View strategies
                </button>
              )}
            />
          ) : (
            <>
              <div className="lg:hidden space-y-1.5">
                {enrichedRows.map((row, i) => (
                  <BrokerPositionCard key={row.id ?? i} row={row} />
                ))}
              </div>
              <div className="hidden lg:block">
                <DataTable columns={brokerColumns} data={enrichedRows} />
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Paper Positions (Paper tab) ───────────────────────────────────── */}
      {posTab === 'paper' && (
        <div className="rounded-lg border border-transparent bg-transparent p-0 lg:border-bg-border lg:bg-bg-panel lg:p-4 lg:shadow-panel">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <p className="hidden lg:block text-2xs uppercase tracking-widest text-txt-label">
              Positions
              {paperViewMode === 'historical' ? (
                <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-amber/10 text-amber text-2xs border border-amber/30">
                  Historical — read only
                </span>
              ) : null}
            </p>
            <div className="flex items-center gap-2">
              <label className="text-2xs text-txt-label">Date:</label>
              <input
                type="date"
                value={paperDate}
                max={todayIst}
                onChange={(e) => setPaperDate(e.target.value)}
                className="text-xs px-2 py-1 rounded border border-bg-border bg-bg-hover/30"
              />
              <button
                type="button"
                onClick={() => setPaperDate('')}
                disabled={!paperDate || paperDate === todayIst}
                className="btn-ghost text-xs py-1 px-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Today
              </button>
            </div>
            {paperViewMode === 'historical' ? (
              <span className="lg:hidden inline-block px-1.5 py-0.5 rounded bg-amber/10 text-amber text-2xs border border-amber/30">
                Historical — read only
              </span>
            ) : null}
          </div>
          {paperPos.isLoading ? (
            <div className="flex justify-center py-12"><Spinner size={18} /></div>
          ) : paperPos.error ? (
            <EmptyState title="Could not load paper positions" description={paperPos.error.message} />
          ) : (paperViewMode === 'historical' ? paperRows : enrichedPaperRows).length === 0 ? (
            paperViewMode === 'historical' ? (
              <EmptyState
                title={`No paper positions on ${paperAsOfDate}`}
                description="Try a different date, or switch back to today."
              />
            ) : (
              <EmptyState
                title="No open paper positions"
                description="Deploy a strategy in Paper mode to see simulated positions here."
                action={(
                  <button onClick={() => navigate(ROUTES.STRATEGIES)} className="btn-secondary mt-4 text-xs">
                    View strategies
                  </button>
                )}
              />
            )
          ) : (
            <>
              <div className="lg:hidden">
                <GroupedPaperPositionsCards rows={paperViewMode === 'historical' ? paperRows : enrichedPaperRows} />
              </div>
              <div className="hidden lg:block">
                <GroupedPaperPositionsTable
                  columns={paperColumns}
                  rows={paperViewMode === 'historical' ? paperRows : enrichedPaperRows}
                />
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

export default function BrokerPositionsPage() {
  return (
    <div className="max-w-7xl space-y-5">
      <SectionHeader title="Position Book" subtitle="Live and paper trading positions." />
      <PositionsBody />
    </div>
  );
}
